import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useWebSocket } from '../../hooks/useWebSocket';
import { usePWA } from '../../hooks/usePWA';
import { PWAInstallBanner, PWAStatusIndicator } from '../../components/pwa/PWAComponents';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { pushBlockReason, pushHelpMessage } from '../../services/pushNotifications';
import {
  Truck, MessageSquare, MapPin, Camera, Send, LogOut,
  Navigation, CheckCircle2, Clock, Package, Loader2, User, X, Bell, Sun, Moon, Volume2, Download,
  Plus, Phone, ClipboardCheck, ChevronRight, ChevronLeft, MoreVertical, FileSignature, BellRing,
  Paperclip, FileText, Image as ImageIcon, Trash2, WifiOff, RefreshCw, CloudUpload, Pencil, Check, Link2, Wrench
} from 'lucide-react';
import { playNotificationSound, requestNotificationPermission, notify } from '../../utils/notifications';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';
import DriverShareCodePanel from './DriverShareCodePanel';
import ReportIssueDialog from './ReportIssueDialog';
import { SwipeableDispatchCard } from './components/SwipeableDispatchCard';
import { FloatingActionButton } from './components/FloatingActionButton';
import { QuickPODModal } from './components/QuickPODModal';

export const DriverView = () => {
  const { api, user, logout, isInTestMode, exitTestMode } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, toggleTheme, isDark } = useTheme();
  const { isInstallable, installApp, requestPushPermission, pushPermission, subscribeToPush } = usePWA('driver');
  const [activeTab, setActiveTab] = useState('dispatches');
  const [showShareCodePanel, setShowShareCodePanel] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedOtherUser, setSelectedOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isTracking, setIsTracking] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showPWABanner, setShowPWABanner] = useState(true);
  const [showQuickPOD, setShowQuickPOD] = useState(false);
  const [selectedDispatchForPOD, setSelectedDispatchForPOD] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [driverNotifications, setDriverNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const messagesEndRef = useRef(null);
  const locationIntervalRef = useRef(null);
  // iter 304.25 — Moving-industry driver_shifts ping (60s heartbeat) so
  // the office Live Drivers tile stays fresh even when the 10s
  // location/update endpoint is silent (e.g., driver stationary and
  // geolocation reports the same fix).
  const shiftPingIntervalRef = useRef(null);
  const photoInputRef = useRef(null);
  const quickPhotoInputRef = useRef(null);
  const prevDispatchCountRef = useRef(0);
  const fetchConversationsRef = useRef(null);
  const fetchDispatchesRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { isOnline, pendingCount, isSyncing, queueFile, retrySync, clearPending } = useOfflineQueue(api);

  // Handle URL parameters from push notification clicks
  useEffect(() => {
    const action = searchParams.get('action');
    const dispatchId = searchParams.get('dispatch_id');
    const tab = searchParams.get('tab');
    
    if (tab) {
      setActiveTab(tab);
    }
    
    if (action && dispatchId && dispatches.length > 0) {
      const targetDispatch = dispatches.find(d => d.id === dispatchId);
      if (targetDispatch) {
        if (action === 'accept') {
          handleAcceptDispatch(dispatchId);
        } else if (action === 'decline') {
          handleDeclineDispatch(dispatchId);
        }
      }
      // Clear the URL params after handling
      setSearchParams({});
    }
  }, [searchParams, dispatches]);

  // Check if push notifications should be prompted
  useEffect(() => {
    // Show prompt on every login if push permission hasn't been granted or denied
    if (pushPermission === 'default') {
      const timer = setTimeout(() => setShowPushPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [pushPermission]);

  // Handle enabling push notifications
  const handleEnablePush = async () => {
    const blocked = pushBlockReason();
    if (blocked) {
      toast.info(pushHelpMessage(blocked), { duration: 12000 });
      if (blocked === 'denied') setShowPushPrompt(false);
      return;
    }
    const granted = await requestPushPermission();
    if (granted) {
      await subscribeToPush();
      toast.success('Push notifications enabled! You\'ll get alerts for new dispatches.');
      
      // Send test notification
      try {
        await api.post('/notifications/test-push');
      } catch (e) {
        // Ignore test push errors
      }
    } else if ('Notification' in window && Notification.permission === 'default') {
      toast.info('No choice was made. Tap Enable again and choose "Allow" when your phone asks.');
      return;
    } else {
      toast.error(pushHelpMessage('denied'), { duration: 12000 });
    }
    setShowPushPrompt(false);
  };

  // Request notification permission - triggered by user action
  useEffect(() => {
    // Check current permission state (don't request yet)
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const handleRequestNotifications = async () => {
    const permitted = await requestNotificationPermission();
    setNotificationsEnabled(permitted);
    if (permitted) {
      toast.success('Notifications enabled');
    } else {
      toast.error('Notification permission denied. Please enable in browser settings.');
    }
  };

  // Fetch in-app notifications
  const fetchDriverNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      const notifs = Array.isArray(res.data) ? res.data : [];
      setDriverNotifications(notifs.slice(0, 20));
      setUnreadNotifCount(notifs.filter(n => !n.read).length);
    } catch (err) {
      // Silent fail - notifications are secondary
    }
  }, []);

  useEffect(() => {
    fetchDriverNotifications();
    const interval = setInterval(fetchDriverNotifications, 60000); // Refresh every 60s
    return () => clearInterval(interval);
  }, [fetchDriverNotifications]);

  // Close notification panel on outside click
  useEffect(() => {
    if (!showNotifPanel) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="driver-notif-bell"]') && !e.target.closest('.notif-panel-container')) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showNotifPanel]);

  const markNotifRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setDriverNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadNotifCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Silent fail
    }
  };

  const markAllNotifsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setDriverNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadNotifCount(0);
    } catch (err) {
      // Silent fail
    }
  };

  const clearAllNotifs = async () => {
    try {
      await api.delete('/notifications/clear-all');
      setDriverNotifications([]);
      setUnreadNotifCount(0);
    } catch (err) {
      // Silent fail
    }
  };

  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const typingTimeoutRef = useRef(null);
  
  // WebSocket for real-time updates
  const handleWebSocketMessage = useCallback((data) => {
    try {
    console.log('WebSocket message received in DriverView:', data.type);
    if (data.type === 'new_message') {
      const msg = data.message;
      console.log('New message received:', msg, 'selectedOtherUser:', selectedOtherUser?.user_id);
      
      // Clear typing indicator when message received
      if (msg.sender_id === selectedOtherUser?.user_id) {
        setOtherUserTyping(false);
      }
      
      // Add new message to list if conversation is open with the sender
      // Check if this message is part of the current open conversation
      const isFromCurrentConversation = selectedOtherUser && (
        msg.sender_id === selectedOtherUser.user_id || // Message from the person we're chatting with
        (msg.sender_id === user?.user_id && msg.receiver_id === selectedOtherUser.user_id) // Our own message to them
      );
      
      if (isFromCurrentConversation) {
        console.log('Adding message to current conversation');
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        
        // Send read receipt if message is from other user
        if (msg.sender_id === selectedOtherUser?.user_id) {
          api.post('/messages/read-receipt', {
            other_user_id: selectedOtherUser.user_id,
            message_ids: [msg.id]
          }).catch(() => {});
        }
      }
      
      // Refresh conversations to update unread count
      if (fetchConversationsRef.current) {
        fetchConversationsRef.current();
      }
      // Note: Sound + browser notification handled by useWebSocket hook
      // No duplicate sound/notify here
    } else if (data.type === 'typing_indicator') {
      // Handle typing indicator
      if (data.sender_id === selectedOtherUser?.user_id) {
        setOtherUserTyping(data.is_typing);
        
        // Auto-clear typing indicator after 5 seconds
        if (data.is_typing) {
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setOtherUserTyping(false);
          }, 5000);
        }
      }
    } else if (data.type === 'messages_read') {
      // Update read status in UI if needed
      console.log('Messages read by:', data.reader_name);
    } else if (data.type === 'message_edited') {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, content: data.content, edited: true, edited_at: data.edited_at } : m
      ));
    } else if (data.type === 'message_deleted') {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, content: 'This message was deleted', deleted: true, attachments: [], file_url: null } : m
      ));
    } else if (data.type === 'dispatch_created' || data.type === 'dispatch_updated' || data.type === 'new_dispatch') {
      // Play sound and notify for new dispatch
      if (soundEnabled) {
        notify('New Dispatch', 'You have a new dispatch assignment', true);
      }
      // Refresh dispatches
      if (fetchDispatchesRef.current) {
        fetchDispatchesRef.current();
      }
      // Also refresh conversations as dispatch creates a message
      if (fetchConversationsRef.current) {
        fetchConversationsRef.current();
      }
    } else if (data.type === 'notification') {
      // Play sound for general notifications
      if (soundEnabled) {
        playNotificationSound();
      }
    }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  }, [selectedOtherUser, soundEnabled, user?.user_id, api]);

  const { connected, reconnect } = useWebSocket(handleWebSocketMessage);
  
  // Send typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (selectedOtherUser?.user_id) {
      api.post('/messages/typing', {
        receiver_id: selectedOtherUser.user_id,
        is_typing: isTyping
      }).catch(() => {});
    }
  }, [api, selectedOtherUser]);
  
  // Handle input change with typing indicator
  const handleMessageInputChange = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) {
      sendTypingIndicator(true);
    }
  };

  // Edit message
  const handleEditMessage = async (messageId) => {
    if (!editContent.trim()) return;
    try {
      await api.put(`/messages/${messageId}`, { content: editContent.trim() });
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content: editContent.trim(), edited: true, edited_at: new Date().toISOString() } : m
      ));
      setEditingMessageId(null);
      setEditContent('');
      toast.success('Message updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit message');
    }
  };

  const canEditMessage = (msg) => {
    if (msg.sender_id !== user?.user_id) return false;
    if (msg.message_type === 'image' || msg.message_type === 'file') return false;
    if (msg.deleted) return false;
    const created = new Date(msg.created_at);
    return (Date.now() - created.getTime()) / 1000 <= 3600;
  };

  const canDeleteMessage = (msg) => {
    if (msg.sender_id !== user?.user_id) return false;
    if (msg.deleted) return false;
    const created = new Date(msg.created_at);
    return (Date.now() - created.getTime()) / 1000 <= 3600;
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await api.delete(`/messages/${messageId}`);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content: 'This message was deleted', deleted: true, attachments: [], file_url: null } : m
      ));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };



  // Check for new dispatches and play sound
  useEffect(() => {
    const waitingDispatches = dispatches.filter(d => d.status === 'WAITING FOR CONFIRMATION');
    if (waitingDispatches.length > prevDispatchCountRef.current && prevDispatchCountRef.current > 0) {
      // New dispatch arrived
      if (soundEnabled) {
        notify('New Dispatch!', 'You have a new dispatch waiting for confirmation', true);
      }
    }
    prevDispatchCountRef.current = waitingDispatches.length;
  }, [dispatches, soundEnabled]);

  const fetchDispatches = useCallback(async () => {
    try {
      const response = await api.get('/dispatches/my');
      setDispatches(response.data || []);
    } catch (error) {
      setDispatches([]);
    }
  }, [api]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/messages/conversations');
      const convos = response.data || [];
      setConversations(convos);
      // Compute total unread message count
      const totalUnread = convos.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadMsgCount(totalUnread);
    } catch (error) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Keep refs updated for WebSocket handler
  useEffect(() => {
    fetchConversationsRef.current = fetchConversations;
  }, [fetchConversations]);

  useEffect(() => {
    fetchDispatchesRef.current = fetchDispatches;
  }, [fetchDispatches]);

  const fetchMessages = useCallback(async (otherUserId) => {
    try {
      const response = await api.get(`/messages/conversation/${otherUserId}`);
      setMessages(response.data || []);
    } catch (error) {
      setMessages([]);
    }
  }, [api]);

  useEffect(() => {
    fetchDispatches();
    fetchConversations();
    checkClockStatus();
  }, [fetchDispatches, fetchConversations]);

  const driverMsgInitialLoadRef = useRef(true);

  // Scroll to bottom - uses requestAnimationFrame with retries for reliability
  const scrollDriverMsgsToBottom = useCallback((instant = false) => {
    let attempts = 0;
    const maxAttempts = 25;
    
    const doScroll = () => {
      attempts++;
      const el = messagesEndRef.current;
      if (el) {
        // First try: direct parent with overflow-y-auto
        const overflowParent = el.closest('.overflow-y-auto') || el.closest('[data-radix-scroll-area-viewport]') || el.parentElement;
        if (overflowParent) {
          overflowParent.scrollTop = overflowParent.scrollHeight;
        }
      }
      // Keep retrying even if ref isn't mounted yet
      if (attempts < maxAttempts) {
        requestAnimationFrame(doScroll);
      }
    };
    
    // Primary scroll after short delay
    const timer = setTimeout(() => {
      requestAnimationFrame(doScroll);
    }, 200);
    
    // Secondary scroll for images/heavy content
    const timer2 = setTimeout(() => {
      const el = messagesEndRef.current;
      if (el) {
        const overflowParent = el.closest('.overflow-y-auto') || el.closest('[data-radix-scroll-area-viewport]') || el.parentElement;
        if (overflowParent) {
          overflowParent.scrollTop = overflowParent.scrollHeight;
        }
      }
    }, 800);
    
    return () => { clearTimeout(timer); clearTimeout(timer2); };
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!messages.length) return;
    const timer = scrollDriverMsgsToBottom(driverMsgInitialLoadRef.current);
    driverMsgInitialLoadRef.current = false;
    return () => clearTimeout(timer);
  }, [messages, scrollDriverMsgsToBottom]);

  // Clock status state
  const [clockStatus, setClockStatus] = useState(null);
  const [isHourlyDriver, setIsHourlyDriver] = useState(false);

  const checkClockStatus = async () => {
    try {
      const response = await api.get('/accounting/clock-status');
      setClockStatus(response.data);
      setIsHourlyDriver(response.data.is_hourly);
      if (response.data.clocked_in) {
        setIsTracking(true);
        startLocationUpdates();
      }
    } catch (error) {
      console.error('Failed to check clock status');
    }
  };

  const handleStartTracking = async () => {
    // Skip actual tracking for super admins in test mode
    if (isInTestMode) {
      setIsTracking(true);
      setClockStatus({
        ...clockStatus,
        clocked_in: true,
        clock_in_time: new Date().toISOString(),
        elapsed_hours: 0
      });
      toast.success('Test Mode: Simulated start working');
      return;
    }
    
    try {
      // Start GPS tracking
      await api.post('/drivers/tracking/start');
      
      // Try to clock in for time tracking (may fail if driver record not set up)
      try {
        const clockResponse = await api.post('/accounting/clock-in');
        setClockStatus({
          ...clockStatus,
          clocked_in: true,
          clock_in_time: clockResponse.data.clock_in_time,
          elapsed_hours: 0
        });
      } catch (clockError) {
        // Clock-in failed but tracking started - this is OK
        console.log('Clock-in not available:', clockError.response?.data?.detail);
      }

      // iter 304.24 — Moving-industry drivers also open a driver_shift so
      // the office Live Drivers tile sees them. Best-effort; if the
      // tenant doesn't have the moving_closings module (403) or the
      // driver_shifts collection rejects it, we still consider the
      // day started.
      const industry = user?.industry_type || user?.company_category;
      if (industry === 'moving' || industry === 'moving_company') {
        try {
          const pos = await new Promise((res) => {
            if (!navigator.geolocation) return res(null);
            navigator.geolocation.getCurrentPosition((p) => res(p), () => res(null), { timeout: 6000 });
          });
          await api.post('/driver-shifts/start', {
            lat: pos ? pos.coords.latitude : null,
            lng: pos ? pos.coords.longitude : null,
          });
        } catch (shiftError) {
          console.log('driver_shifts start not available:', shiftError.response?.data?.detail);
        }
        // iter 304.25 — Kick off the 60s heartbeat.
        startShiftPing();
      }

      setIsTracking(true);
      toast.success('Started working - GPS tracking active');
      startLocationUpdates();
    } catch (error) {
      console.error('Start tracking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start working');
    }
  };

  const handleStopTracking = async () => {
    // Skip actual tracking for super admins in test mode
    if (isInTestMode) {
      setIsTracking(false);
      setClockStatus({ ...clockStatus, clocked_in: false });
      toast.success('Test Mode: Simulated stop working');
      return;
    }
    
    try {
      // Stop GPS tracking
      await api.post('/drivers/tracking/stop');
      
      // Try to clock out for time tracking
      let clockResponse = null;
      try {
        clockResponse = await api.post('/accounting/clock-out');
      } catch (clockError) {
        console.log('Clock-out not available:', clockError.response?.data?.detail);
      }

      // iter 304.24 — Close the driver_shift for Moving-industry drivers
      // so the office Live Drivers tile drops them.
      const industry = user?.industry_type || user?.company_category;
      if (industry === 'moving' || industry === 'moving_company') {
        try { await api.post('/driver-shifts/end', {}); }
        catch (shiftError) { console.log('driver_shifts end not available:', shiftError.response?.data?.detail); }
        // iter 304.25 — Stop the heartbeat.
        stopShiftPing();
      }
      
      setIsTracking(false);
      setClockStatus({ ...clockStatus, clocked_in: false });
      
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
      
      // Show summary for hourly drivers
      if (isHourlyDriver && clockResponse?.data?.total_hours) {
        const { total_hours, straight_hours, ot_hours, double_hours, total_pay } = clockResponse.data;
        toast.success(
          `Day finished! ${total_hours.toFixed(1)}h worked\n` +
          `Straight: ${straight_hours.toFixed(1)}h | OT: ${ot_hours.toFixed(1)}h | Double: ${double_hours.toFixed(1)}h\n` +
          `Total Pay: $${total_pay.toFixed(2)}`,
          { duration: 8000 }
        );
      } else {
        toast.success('Day finished - Tracking stopped');
      }
    } catch (error) {
      console.error('Stop tracking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to stop working');
    }
  };

  const startLocationUpdates = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    const updateLocation = () => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await api.post('/drivers/location/update', {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              speed: position.coords.speed,
              heading: position.coords.heading
            });
          } catch (error) {
            console.error('Failed to update location');
          }
        },
        (error) => console.error('Geolocation error:', error),
        { enableHighAccuracy: true }
      );
    };

    updateLocation();
    // Update location every 10 seconds for better tracking
    locationIntervalRef.current = setInterval(updateLocation, 10000);
  };

  // iter 304.25 — Moving-industry driver_shifts heartbeat. Fires
  // `POST /driver-shifts/ping` every 60s with the current GPS fix so
  // the office map stays fresh even when the 10s `/drivers/location/
  // update` endpoint returns the same coords (stationary driver).
  //
  // iter 304.26 — Battery-save adaptive schedule. We keep a rolling
  // window of the last 15 pings (~15 minutes at the fast cadence). If
  // every point in the last 10 min is within 30 m of the newest fix,
  // the driver is stationary and we slow the heartbeat to 3 minutes so
  // a 12-hour shift doesn't drain the phone. Any movement snaps back
  // to 60 s immediately.
  const pingHistoryRef = useRef([]);
  const pingScheduleRef = useRef({ intervalMs: 60000, stationary: false });
  const FAST_MS = 60000;
  const SLOW_MS = 180000;
  const STATIONARY_MIN_MS = 10 * 60000;
  const STATIONARY_RADIUS_M = 30;

  // Haversine distance in metres between two lat/lng points.
  const distMeters = (a, b) => {
    if (!a || !b) return Infinity;
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const startShiftPing = () => {
    if (shiftPingIntervalRef.current) return;
    const industry = user?.industry_type || user?.company_category;
    if (industry !== 'moving' && industry !== 'moving_company') return;
    pingHistoryRef.current = [];
    pingScheduleRef.current = { intervalMs: FAST_MS, stationary: false };

    const evaluateCadence = () => {
      const hist = pingHistoryRef.current;
      if (hist.length < 2) return FAST_MS;
      const newest = hist[hist.length - 1];
      // Points inside the 10 min window.
      const cutoff = newest.at - STATIONARY_MIN_MS;
      const inWindow = hist.filter((p) => p.at >= cutoff);
      // Need at least ~10 min of samples before we call it stationary.
      const windowSpan = newest.at - inWindow[0].at;
      if (windowSpan < STATIONARY_MIN_MS - 30000) return FAST_MS;
      const stationary = inWindow.every((p) => distMeters(p, newest) <= STATIONARY_RADIUS_M);
      pingScheduleRef.current.stationary = stationary;
      return stationary ? SLOW_MS : FAST_MS;
    };

    const scheduleNext = (nextMs) => {
      if (!shiftPingIntervalRef.current) return; // stopped
      // Reuse the same ref as a timeout handle.
      shiftPingIntervalRef.current = setTimeout(runOnce, nextMs);
    };

    const runOnce = () => {
      const send = (lat, lng) => {
        const body = lat != null && lng != null ? { lat, lng } : {};
        api.post('/driver-shifts/ping', body).catch(() => {});
        if (lat != null && lng != null) {
          const now = Date.now();
          const hist = pingHistoryRef.current;
          hist.push({ lat, lng, at: now });
          // Keep 20 samples max (~20 min at fast, ~60 min at slow).
          if (hist.length > 20) hist.shift();
        }
        const nextMs = evaluateCadence();
        pingScheduleRef.current.intervalMs = nextMs;
        scheduleNext(nextMs);
      };
      if (!navigator.geolocation) return send(null, null);
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos.coords.latitude, pos.coords.longitude),
        () => send(null, null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    };

    // Kick off with a synthetic ref so `scheduleNext` sees an active
    // session; then fire the first ping immediately.
    shiftPingIntervalRef.current = setTimeout(() => {}, 0);
    runOnce();
  };

  const stopShiftPing = () => {
    if (shiftPingIntervalRef.current) {
      clearTimeout(shiftPingIntervalRef.current);
      shiftPingIntervalRef.current = null;
    }
    pingHistoryRef.current = [];
    pingScheduleRef.current = { intervalMs: FAST_MS, stationary: false };
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
      // iter 304.25 — Also clear the shift ping heartbeat on unmount so
      // navigating away doesn't leak pings. iter 304.26 uses setTimeout
      // now, so clearTimeout is the correct cleanup.
      if (shiftPingIntervalRef.current) {
        clearTimeout(shiftPingIntervalRef.current);
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && pendingPhotos.length === 0) || !selectedOtherUser) return;

    // Clear typing indicator when sending
    sendTypingIndicator(false);
    
    setSendingMessage(true);
    try {
      const hasAttachments = pendingPhotos.length > 0;
      const fileLabel = pendingPhotos.length === 1 
        ? (pendingPhotos[0].is_image ? '📷 Photo' : '📎 Document')
        : `📎 ${pendingPhotos.length} files`;
      
      const messageData = {
        receiver_id: selectedOtherUser.user_id,
        content: newMessage.trim() || (hasAttachments ? fileLabel : ''),
        message_type: hasAttachments ? (pendingPhotos.every(p => p.is_image) ? 'image' : 'file') : 'text',
        file_url: hasAttachments ? pendingPhotos[0].file_id : null
      };
      
      // Include all attachments
      if (hasAttachments) {
        messageData.attachments = pendingPhotos.map(p => ({
          file_id: p.file_id,
          filename: p.filename,
          is_image: p.is_image,
          content_type: p.content_type
        }));
      }
      
      await api.post('/messages/send', messageData);
      setNewMessage('');
      setPendingPhotos([]);
      fetchMessages(selectedOtherUser.user_id);
    } catch (error) {
      if (!navigator.onLine) {
        toast.info('Message will be sent when back online');
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate each file (max 10MB per file, max 10 files)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const MAX_FILES = 10;
    
    const totalPending = pendingPhotos.length + files.length;
    if (totalPending > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files at once. You have ${pendingPhotos.length} pending.`);
      return;
    }

    const oversized = files.filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} file(s) exceed 10MB limit`);
      return;
    }

    setUploadingPhoto(true);
    const uploaded = [];
    
    for (const file of files) {
      if (!isOnline) {
        // Queue file for later upload when back online
        // Fallback to dispatcher if no conversation is selected
        const receiverId = selectedOtherUser?.user_id 
          || conversations.find(c => c.other_user?.role === 'dispatcher' || c.other_user?.role === 'admin')?.other_user?.user_id;
        const queued = await queueFile(file, receiverId, 'message');
        if (queued) {
          uploaded.push({ filename: file.name, is_image: file.type.startsWith('image/'), offline: true });
        }
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/messages/upload-attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const isImage = file.type.startsWith('image/');
        uploaded.push({
          file_id: response.data.file_id,
          filename: response.data.filename || file.name,
          is_image: isImage,
          content_type: file.type
        });
      } catch (error) {
        // If upload fails (could be network drop), queue offline
        const receiverId = selectedOtherUser?.user_id
          || conversations.find(c => c.other_user?.role === 'dispatcher' || c.other_user?.role === 'admin')?.other_user?.user_id;
        const queued = await queueFile(file, receiverId, 'message');
        if (queued) {
          uploaded.push({ filename: file.name, is_image: file.type.startsWith('image/'), offline: true });
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    }
    
    const offlineItems = uploaded.filter(u => u.offline);
    const onlineItems = uploaded.filter(u => !u.offline);
    
    if (onlineItems.length > 0) {
      setPendingPhotos(prev => [...prev, ...onlineItems]);
      toast.success(`${onlineItems.length} file${onlineItems.length > 1 ? 's' : ''} ready to send`);
    }
    if (offlineItems.length > 0) {
      toast.info(`${offlineItems.length} file${offlineItems.length > 1 ? 's' : ''} saved offline. Will send when back online.`);
    }
    
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const getFileUrl = (fileId) => `${API_URL}/api/files/${fileId}`;

  // Dispatch actions
  const handleAcceptDispatch = async (dispatchId) => {
    setActionLoading(dispatchId);
    try {
      await api.post(`/dispatches/${dispatchId}/accept`);
      toast.success('Dispatch accepted');
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineDispatch = async (dispatchId) => {
    setActionLoading(dispatchId);
    try {
      await api.post(`/dispatches/${dispatchId}/decline`);
      toast.success('Dispatch declined');
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartDispatch = async (dispatchId) => {
    setActionLoading(dispatchId);
    try {
      await api.post(`/dispatches/${dispatchId}/start`);
      toast.success('Dispatch started');
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompleteDispatch = async (dispatchId) => {
    setActionLoading(dispatchId);
    try {
      await api.post(`/dispatches/${dispatchId}/complete`);
      toast.success('Dispatch completed!');
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WAITING FOR CONFIRMATION': return 'bg-amber-600';
      case 'ACCEPTED': return 'bg-blue-600';
      case 'STARTED': return 'bg-green-600';
      case 'DONE': return 'bg-slate-600';
      case 'DECLINED': return 'bg-red-600';
      default: return 'bg-slate-600';
    }
  };

  // Handle FAB actions
  const handleFABAction = (action) => {
    switch (action) {
      case 'photo':
        quickPhotoInputRef.current?.click();
        break;
      case 'call': {
        // Get dispatcher phone from first conversation or default
        const dispatcherConv = conversations.find(c => c.other_user?.role === 'dispatcher' || c.other_user?.role === 'admin');
        if (dispatcherConv?.other_user?.phone) {
          window.location.href = `tel:${dispatcherConv.other_user.phone}`;
        } else {
          toast.info('No dispatcher phone number available');
        }
        break;
      }
      case 'pod': {
        // Open quick POD for first active dispatch
        const activeDispatch = dispatches.find(d => d.status === 'STARTED');
        if (activeDispatch) {
          setSelectedDispatchForPOD(activeDispatch);
          setShowQuickPOD(true);
        } else {
          toast.info('No active dispatch for POD. Start a dispatch first.');
        }
        break;
      }
      default:
        break;
    }
  };

  // Handle quick photo capture (FAB action - sends directly to dispatcher)
  const handleQuickPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const validFiles = files.filter(f => f.size <= MAX_FILE_SIZE);
    if (validFiles.length < files.length) {
      toast.error(`${files.length - validFiles.length} file(s) skipped (exceed 10MB)`);
    }
    if (validFiles.length === 0) return;

    // Find dispatcher to send to
    const dispatcherConv = conversations.find(c => c.other_user?.role === 'dispatcher' || c.other_user?.role === 'admin');
    if (!dispatcherConv?.other_user?.user_id) {
      toast.error('No dispatcher found to send files');
      return;
    }

    setUploadingPhoto(true);
    const uploaded = [];
    let offlineQueued = 0;

    for (const file of validFiles) {
      if (!isOnline) {
        const queued = await queueFile(file, dispatcherConv.other_user.user_id, 'quick_photo');
        if (queued) offlineQueued++;
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/messages/upload-attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push({
          file_id: response.data.file_id,
          filename: response.data.filename || file.name,
          is_image: file.type.startsWith('image/'),
          content_type: file.type
        });
      } catch (error) {
        // Network failure during upload — queue offline
        const queued = await queueFile(file, dispatcherConv.other_user.user_id, 'quick_photo');
        if (queued) {
          offlineQueued++;
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    }

    if (offlineQueued > 0) {
      toast.info(`${offlineQueued} file${offlineQueued > 1 ? 's' : ''} saved offline. Will send when back online.`);
    }

    if (uploaded.length > 0) {
      try {
        const fileLabel = uploaded.length === 1
          ? (uploaded[0].is_image ? '📷 Photo from driver' : '📎 Document from driver')
          : `📎 ${uploaded.length} files from driver`;

        await api.post('/messages/send', {
          receiver_id: dispatcherConv.other_user.user_id,
          content: fileLabel,
          message_type: uploaded.every(u => u.is_image) ? 'image' : 'file',
          file_url: uploaded[0].file_id,
          attachments: uploaded.map(u => ({
            file_id: u.file_id,
            filename: u.filename,
            is_image: u.is_image,
            content_type: u.content_type
          }))
        });
        toast.success(`${uploaded.length} file${uploaded.length > 1 ? 's' : ''} sent to dispatcher`);
      } catch (error) {
        toast.error('Failed to send files');
      }
    }

    setUploadingPhoto(false);
    if (quickPhotoInputRef.current) quickPhotoInputRef.current.value = '';
  };

  // Handle opening POD for a specific dispatch
  const handleOpenPOD = (dispatch) => {
    // Navigate to POD page with shipment pre-selected
    navigate(`/pod?shipment=${dispatch.shipment_id || dispatch.id}&container=${dispatch.container_number || ''}`);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* PWA Install Banner */}
      {showPWABanner && <PWAInstallBanner appType="driver" onDismiss={() => setShowPWABanner(false)} />}
      
      {/* Push Notification Prompt */}
      {showPushPrompt && (
        <div className="bg-gradient-to-r from-blue-900 to-purple-900 border-b border-blue-500/30 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <BellRing className="h-5 w-5 text-blue-400 animate-pulse" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Enable Push Notifications</p>
                <p className="text-blue-200 text-xs">Get instant alerts for new dispatches</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowPushPrompt(false);
                }}
                className="text-slate-300 hover:text-white"
              >
                Later
              </Button>
              <Button
                size="sm"
                onClick={handleEnablePush}
                className="bg-blue-500 hover:bg-blue-400"
                data-testid="enable-push-btn"
              >
                Enable
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden bg-slate-900">
              <img src="/logo192.png" alt="Drayly" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-white">Drayly Driver</h1>
              <div className="text-xs text-slate-400 flex items-center gap-1">
                <PWAStatusIndicator appType="driver" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Offline Indicator */}
            {!isOnline && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-900/60 border border-amber-700/50 rounded-full" data-testid="offline-indicator">
                <WifiOff className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-xs text-amber-300 font-medium">Offline</span>
              </div>
            )}
            {/* Pending Queue Badge */}
            {pendingCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={retrySync}
                disabled={isSyncing}
                className="relative text-blue-400 hover:text-blue-300 px-2 gap-1"
                title={isSyncing ? 'Syncing...' : `${pendingCount} pending upload${pendingCount > 1 ? 's' : ''} — tap to retry`}
                data-testid="pending-sync-btn"
              >
                {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
                <span className="text-xs font-bold">{pendingCount}</span>
              </Button>
            )}
            {/* Install App Button */}
            {isInstallable && (
              <Button
                variant="ghost"
                size="icon"
                onClick={installApp}
                className="text-blue-400 hover:text-blue-300"
                title="Install App"
              >
                <Download className="h-5 w-5" />
              </Button>
            )}
            {/* Notifications Bell */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowNotifPanel(!showNotifPanel);
                  fetchDriverNotifications();
                }}
                className={`${unreadNotifCount > 0 ? 'text-blue-400' : 'text-slate-500'} hover:text-white`}
                data-testid="driver-notif-bell"
                title="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadNotifCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                  </span>
                )}
              </Button>
              
              {/* Notification Dropdown */}
              {showNotifPanel && (
                <div className="fixed inset-x-0 top-[65px] mx-2 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:mx-0 sm:w-80 max-h-[70vh] bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50 notif-panel-container">
                  <div className="p-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
                    <span className="text-sm font-semibold text-white">Notifications</span>
                    <div className="flex items-center gap-1">
                      {driverNotifications.length > 0 && (
                        <>
                          {unreadNotifCount > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={markAllNotifsRead}
                              className="h-6 px-2 text-[10px] text-blue-400 hover:text-blue-300"
                              data-testid="mark-all-read-btn"
                            >
                              Mark read
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearAllNotifs}
                            className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300"
                            data-testid="clear-all-notifs-btn"
                          >
                            Clear all
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNotifPanel(false)}
                        className="h-6 w-6 p-0 text-slate-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(70vh-50px)]">
                  {driverNotifications.length === 0 ? (
                    <div className="p-6 text-center text-slate-500 text-sm">No notifications</div>
                  ) : (
                    driverNotifications.map(notif => (
                      <div
                        key={notif.id}
                        className={`p-3 sm:p-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 ${!notif.read ? 'bg-blue-900/10' : ''}`}
                        onClick={() => markNotifRead(notif.id)}
                        data-testid={`notif-item-${notif.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {!notif.read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{notif.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{notif.message}</p>
                            <p className="text-[10px] text-slate-600 mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  </div>
                  {/* Push Notification Enable */}
                  {pushPermission !== 'granted' && (
                    <div className="p-3 border-t border-slate-700 bg-slate-800/50">
                      <Button
                        size="sm"
                        onClick={handleEnablePush}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-xs"
                        data-testid="enable-push-from-panel"
                      >
                        <BellRing className="h-3.5 w-3.5 mr-1.5" />
                        Enable Push Notifications
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Sound Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`${soundEnabled ? 'text-green-400' : 'text-slate-500'} hover:text-white`}
              data-testid="sound-toggle-btn"
              title={soundEnabled ? 'Notifications ON' : 'Notifications OFF'}
            >
              <Volume2 className="h-5 w-5" />
            </Button>
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-slate-400 hover:text-white"
              data-testid="driver-theme-toggle-btn"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            {/* Connection status with reconnect */}
            <div 
              className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer ${connected ? 'bg-green-500/20' : 'bg-red-500/20'}`}
              onClick={!connected ? reconnect : undefined}
              title={connected ? 'Connected' : 'Disconnected - Click to reconnect'}
            >
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <span className={`text-xs ${connected ? 'text-green-400' : 'text-red-400'}`}>
                {connected ? 'Live' : 'Tap to reconnect'}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-white">{user?.name}</p>
              <Badge variant="outline" className={isTracking ? 'text-green-400 border-green-500' : 'text-slate-400'}>
                {isTracking ? 'Tracking' : 'Offline'}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowReportIssue(true)}
              className="text-slate-400 hover:text-orange-400"
              title="Report issue"
              data-testid="driver-open-report-issue-btn"
            >
              <Wrench className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowShareCodePanel(true)}
              className="text-slate-400 hover:text-blue-400"
              title="Work with more companies"
              data-testid="driver-open-share-code-btn"
            >
              <Link2 className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-slate-400"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Test Mode Banner - Always visible when in test mode */}
      {isInTestMode && (
        <div className="bg-gradient-to-r from-red-900 to-orange-900 border-b-2 border-red-500 p-3 relative z-50">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { 
                exitTestMode(); 
                window.location.href = '/super-admin';
              }}
              className="bg-white hover:bg-gray-100 text-red-600 border-white font-semibold"
              data-testid="exit-test-mode-btn"
            >
              <X className="h-4 w-4 mr-1" />
              Exit Test Mode
            </Button>
            <div className="flex items-center gap-2 text-white">
              <span className="animate-pulse text-lg">🧪</span>
              <span className="font-medium">Testing as Driver</span>
              <span className="text-red-200 text-sm">(Super Admin)</span>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Controls */}
      <div className="p-4 bg-slate-900/50">
        {/* Time tracking info for hourly drivers */}
        {isTracking && clockStatus?.clocked_in && (
          <div className="mb-3 p-3 bg-slate-800 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Working since {new Date(clockStatus.clock_in_time).toLocaleTimeString()}</span>
              <span className="text-white font-medium">{clockStatus.elapsed_hours?.toFixed(1) || 0}h</span>
            </div>
            {isHourlyDriver && (
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div className="text-center">
                  <div className="text-slate-400">Straight</div>
                  <div className="text-white font-medium">{clockStatus.straight_hours?.toFixed(1) || 0}h</div>
                </div>
                <div className="text-center">
                  <div className="text-yellow-400">OT</div>
                  <div className="text-yellow-400 font-medium">{clockStatus.ot_hours?.toFixed(1) || 0}h</div>
                </div>
                <div className="text-center">
                  <div className="text-red-400">Double</div>
                  <div className="text-red-400 font-medium">{clockStatus.double_hours?.toFixed(1) || 0}h</div>
                </div>
              </div>
            )}
            {isHourlyDriver && clockStatus.projected_pay !== null && (
              <div className="mt-2 text-center text-green-400 font-semibold">
                Projected: ${clockStatus.projected_pay?.toFixed(2)}
              </div>
            )}
            {/* HOS Status */}
            {clockStatus.hos?.enrolled && (
              <div className="mt-2 pt-2 border-t border-slate-700">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-400">HOS ({clockStatus.hos.rule_type === 'ca_intrastate' ? 'CA Intra' : clockStatus.hos.rule_type?.toUpperCase()})</span>
                  {clockStatus.hos.has_violations && (
                    <span className="text-red-400 font-bold">VIOLATION</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="text-slate-400">Drive</div>
                    <div className={`font-medium ${clockStatus.hos.driving_remaining <= 1 ? 'text-red-400' : clockStatus.hos.driving_remaining <= 2 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {clockStatus.hos.driving_remaining?.toFixed(1)}h
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Duty</div>
                    <div className={`font-medium ${clockStatus.hos.on_duty_remaining <= 1 ? 'text-red-400' : clockStatus.hos.on_duty_remaining <= 2 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {clockStatus.hos.on_duty_remaining?.toFixed(1)}h
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Weekly</div>
                    <div className={`font-medium ${clockStatus.hos.weekly_remaining <= 5 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {clockStatus.hos.weekly_remaining?.toFixed(1)}h
                    </div>
                  </div>
                </div>
                {clockStatus.hos.alerts?.length > 0 && (
                  <div className="mt-1 text-[10px] text-amber-400">
                    {clockStatus.hos.alerts[0]?.message}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-3">
          {!isTracking ? (
            <Button
              onClick={handleStartTracking}
              className="flex-1 bg-green-600 hover:bg-green-500"
              data-testid="start-tracking-btn"
            >
              <Navigation className="h-4 w-4 mr-2" />
              Start Working
            </Button>
          ) : (
            <Button
              onClick={handleStopTracking}
              className="flex-1 bg-red-600 hover:bg-red-500"
              data-testid="stop-tracking-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Finish Working
            </Button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-slate-800">
        <button
          onClick={() => setActiveTab('dispatches')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
            activeTab === 'dispatches'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400'
          }`}
        >
          <Package className="h-4 w-4" />
          Dispatches
          {dispatches.length > 0 && (
            <Badge className="bg-blue-600 h-5 px-1.5">{dispatches.length}</Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
            activeTab === 'messages'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400'
          }`}
        >
          <MessageSquare className="h-4 w-4" />
          Messages
          {unreadMsgCount > 0 && (
            <Badge className="bg-red-500 h-5 px-1.5">{unreadMsgCount > 99 ? '99+' : unreadMsgCount}</Badge>
          )}
        </button>
        {/* iter 304.24 — Moving-industry drivers get a Closings shortcut
            that opens their scoped mobile Closings view (backend already
            filters to driver_id == user_id). */}
        {(user?.industry_type === 'moving' || user?.company_category === 'moving_company') && (
          <button
            onClick={() => navigate('/moving/closings/mobile')}
            className="flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 text-slate-400 hover:text-emerald-300"
            data-testid="driver-closings-tab"
          >
            <ClipboardCheck className="h-4 w-4" />
            Closings
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 pb-24">
        {/* Hidden quick photo/document input */}
        <input
          type="file"
          ref={quickPhotoInputRef}
          onChange={handleQuickPhoto}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
          multiple
        />
        
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : activeTab === 'dispatches' ? (
          /* Dispatches Tab - Mobile Optimized */
          <div className="space-y-4">
            {/* Active dispatch highlight */}
            {dispatches.filter(d => d.status === 'STARTED').length > 0 && (
              <div className="mb-4">
                <h3 className="text-amber-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <Navigation className="h-4 w-4 animate-pulse" />
                  IN PROGRESS
                </h3>
                {dispatches.filter(d => d.status === 'STARTED').map(dispatch => (
                  <SwipeableDispatchCard
                    key={dispatch.id}
                    dispatch={dispatch}
                    onAccept={handleAcceptDispatch}
                    onDecline={handleDeclineDispatch}
                    onStart={handleStartDispatch}
                    onComplete={handleCompleteDispatch}
                    onPOD={handleOpenPOD}
                    actionLoading={actionLoading}
                    getStatusColor={getStatusColor}
                  />
                ))}
              </div>
            )}
            
            {/* Pending dispatches */}
            {dispatches.filter(d => d.status === 'WAITING FOR CONFIRMATION').length > 0 && (
              <div className="mb-4">
                <h3 className="text-slate-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  AWAITING RESPONSE ({dispatches.filter(d => d.status === 'WAITING FOR CONFIRMATION').length})
                </h3>
                <div className="space-y-3">
                  {dispatches.filter(d => d.status === 'WAITING FOR CONFIRMATION').map(dispatch => (
                    <SwipeableDispatchCard
                      key={dispatch.id}
                      dispatch={dispatch}
                      onAccept={handleAcceptDispatch}
                      onDecline={handleDeclineDispatch}
                      onStart={handleStartDispatch}
                      onComplete={handleCompleteDispatch}
                      onPOD={handleOpenPOD}
                      actionLoading={actionLoading}
                      getStatusColor={getStatusColor}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Accepted dispatches */}
            {dispatches.filter(d => d.status === 'ACCEPTED').length > 0 && (
              <div className="mb-4">
                <h3 className="text-blue-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  READY TO START ({dispatches.filter(d => d.status === 'ACCEPTED').length})
                </h3>
                <div className="space-y-3">
                  {dispatches.filter(d => d.status === 'ACCEPTED').map(dispatch => (
                    <SwipeableDispatchCard
                      key={dispatch.id}
                      dispatch={dispatch}
                      onAccept={handleAcceptDispatch}
                      onDecline={handleDeclineDispatch}
                      onStart={handleStartDispatch}
                      onComplete={handleCompleteDispatch}
                      onPOD={handleOpenPOD}
                      actionLoading={actionLoading}
                      getStatusColor={getStatusColor}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Completed dispatches (last 3) */}
            {dispatches.filter(d => d.status === 'DONE').length > 0 && (
              <div className="mt-6">
                <h3 className="text-slate-500 text-sm font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  COMPLETED TODAY
                </h3>
                <div className="space-y-2">
                  {dispatches.filter(d => d.status === 'DONE').slice(0, 3).map(dispatch => (
                    <div key={dispatch.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                        <div>
                          <p className="text-slate-300 font-mono text-sm">{dispatch.container_number || 'N/A'}</p>
                          <p className="text-slate-500 text-xs">{dispatch.destination || 'Delivered'}</p>
                        </div>
                      </div>
                      <Badge className="bg-slate-700 text-slate-300">Done</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {dispatches.length === 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="py-10 text-center">
                  <Package className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No dispatches assigned</p>
                  <p className="text-sm text-slate-500 mt-1">You'll see new dispatches here</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Messages Tab */
          <div className="space-y-3">
            {!selectedConversation ? (
              /* Conversation List */
              conversations.length === 0 ? (
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="py-10 text-center">
                    <MessageSquare className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No messages yet</p>
                  </CardContent>
                </Card>
              ) : (
                conversations.map(conv => (
                  <Card
                    key={conv.conversation_id}
                    data-testid={`conversation-item-${conv.other_user?.user_id}`}
                    className={`cursor-pointer hover:bg-slate-800/50 transition-colors ${
                      conv.unread_count > 0 
                        ? 'bg-red-900/10 border-red-500/40 border-l-2 border-l-red-500' 
                        : 'bg-slate-900 border-slate-800'
                    }`}
                    onClick={() => {
                      driverMsgInitialLoadRef.current = true;
                      setSelectedConversation(conv.conversation_id);
                      setSelectedOtherUser(conv.other_user);
                      fetchMessages(conv.other_user?.user_id);
                    }}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className={`font-medium ${conv.unread_count > 0 ? 'text-white font-semibold' : 'text-white'}`}>{conv.other_user?.name || 'Dispatcher'}</p>
                            <p className={`text-sm truncate max-w-[200px] ${conv.unread_count > 0 ? 'text-slate-300 font-medium' : 'text-slate-400'}`}>
                              {conv.last_message?.content || 'No messages'}
                            </p>
                          </div>
                        </div>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-red-500 text-white font-bold" data-testid={`unread-badge-${conv.conversation_id}`}>{conv.unread_count}</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )
            ) : (
              /* Chat View */
              <div className="flex flex-col h-[calc(100vh-280px)]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedConversation(null);
                    setSelectedOtherUser(null);
                    setMessages([]);
                    setPendingPhotos([]);
                    // Refresh conversations to update read status
                    fetchConversations();
                  }}
                  className="text-slate-400 mb-3 self-start"
                >
                  ← Back to conversations
                </Button>
                
                {/* Messages with Image Support */}
                <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-2">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        msg.sender_id === user?.user_id
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-800 text-white'
                      }`}>
                        {/* Render photo attachments from attachments array */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mb-2">
                            {msg.attachments.map((att, idx) => (
                              att.is_image && (
                                <img
                                  key={idx}
                                  src={getFileUrl(att.file_id)}
                                  alt={att.filename}
                                  className="max-w-full rounded-lg cursor-pointer mb-2"
                                  style={{ maxHeight: '180px' }}
                                  onClick={() => setPreviewImage(getFileUrl(att.file_id))}
                                />
                              )
                            ))}
                          </div>
                        )}
                        {/* Fallback: Render image from file_url if no attachments (legacy messages) */}
                        {(!msg.attachments || msg.attachments.length === 0) && msg.message_type === 'image' && msg.file_url && (
                          <div className="mb-2">
                            <img
                              src={getFileUrl(msg.file_url)}
                              alt="Photo"
                              className="max-w-full rounded-lg cursor-pointer mb-2"
                              style={{ maxHeight: '180px' }}
                              onClick={() => setPreviewImage(getFileUrl(msg.file_url))}
                            />
                          </div>
                        )}
                        {msg.content && msg.content !== '📷 Photo' && (
                          editingMessageId === msg.id ? (
                            <div className="flex gap-1 items-center">
                              <input
                                type="text"
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleEditMessage(msg.id);
                                  if (e.key === 'Escape') { setEditingMessageId(null); setEditContent(''); }
                                }}
                                className="flex-1 bg-black/20 text-white text-sm rounded px-2 py-1 border border-white/20 outline-none"
                                autoFocus
                                data-testid="edit-message-input"
                              />
                              <button onClick={() => handleEditMessage(msg.id)} className="text-green-300 p-0.5"><Check className="h-4 w-4" /></button>
                              <button onClick={() => { setEditingMessageId(null); setEditContent(''); }} className="text-red-300 p-0.5"><X className="h-3.5 w-3.5" /></button>
                            </div>
                          ) : (
                            <div className="relative group/msg">
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              {msg.edited && !msg.deleted && <span className="text-[10px] opacity-50 italic">edited</span>}
                              {!msg.deleted && (canEditMessage(msg) || canDeleteMessage(msg)) && (
                                <div className="absolute -top-2 -right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex gap-0.5">
                                  {canEditMessage(msg) && (
                                    <button onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); }}
                                      className="bg-slate-700 rounded-full p-1 shadow" data-testid={`edit-msg-${msg.id}`}>
                                      <Pencil className="h-3 w-3 text-slate-300" />
                                    </button>
                                  )}
                                  {canDeleteMessage(msg) && (
                                    <button onClick={() => handleDeleteMessage(msg.id)}
                                      className="bg-slate-700 rounded-full p-1 shadow hover:bg-red-700" data-testid={`delete-msg-${msg.id}`}>
                                      <Trash2 className="h-3 w-3 text-slate-300" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        )}
                        <p className="text-xs opacity-70 mt-1">
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {otherUserTyping && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] p-3 rounded-lg bg-slate-800 text-white">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                          </div>
                          <span className="text-xs text-slate-400">{selectedOtherUser?.name} is typing...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Pending Attachments Preview */}
                {pendingPhotos.length > 0 && (
                  <div className="mb-2 p-2 bg-slate-800 rounded-lg space-y-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-400 font-medium">
                        {pendingPhotos.length} file{pendingPhotos.length > 1 ? 's' : ''} ready to send
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingPhotos([])}
                        className="h-5 px-1 text-slate-500 hover:text-red-400 text-xs"
                        data-testid="clear-all-attachments"
                      >
                        Clear all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pendingPhotos.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 bg-slate-700 rounded-md px-2 py-1 text-xs">
                          {file.is_image 
                            ? <ImageIcon className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
                            : <FileText className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                          }
                          <span className="text-slate-300 truncate max-w-[100px]">{file.filename}</span>
                          <button
                            onClick={() => setPendingPhotos(prev => prev.filter((_, i) => i !== idx))}
                            className="text-slate-500 hover:text-red-400 ml-0.5"
                            data-testid={`remove-attachment-${idx}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Message Input with Photo Upload */}
                <div className="flex gap-2 pt-2 border-t border-slate-800">
                  <input
                    type="file"
                    ref={photoInputRef}
                    onChange={handlePhotoUpload}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
                    multiple
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-slate-700"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    data-testid="driver-attach-btn"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={handleMessageInputChange}
                    placeholder={pendingPhotos.length > 0 ? "Add caption..." : "Type a message..."}
                    className="flex-1 bg-slate-900 border-slate-700"
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    onBlur={() => sendTypingIndicator(false)}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || (!newMessage.trim() && pendingPhotos.length === 0)}
                    className="bg-blue-600"
                  >
                    {sendingMessage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-4xl max-h-4xl p-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 text-white hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </Button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      {activeTab === 'dispatches' && (
        <FloatingActionButton 
          onAction={handleFABAction}
          isTracking={isTracking}
        />
      )}

      {/* Quick POD Modal */}
      <QuickPODModal
        open={showQuickPOD}
        dispatch={selectedDispatchForPOD}
        onClose={() => setShowQuickPOD(false)}
        onOpenFullPOD={handleOpenPOD}
        onQuickPhoto={() => quickPhotoInputRef.current?.click()}
        onComplete={handleCompleteDispatch}
        actionLoading={actionLoading}
      />

      {/* Driver Share Code Panel */}
      <DriverShareCodePanel open={showShareCodePanel} onOpenChange={setShowShareCodePanel} />

      {/* Report Equipment Issue Dialog */}
      <ReportIssueDialog open={showReportIssue} onOpenChange={setShowReportIssue} />
    </div>
  );
};

export default DriverView;
